import React, { useEffect, useRef, useState } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { Message, Provider } from "../km"
import { scrollbarMixin } from "src/providers/AppProviders"
import { BotIcon } from "./BotIcon"
import LoadingDots from "./ui/LoadingDots"
import { BotMessage, UserMessage } from "./KmMessageList"

interface ChatBotMessageListProps {
  messages: Message[]
  botIcon: string
  loading: boolean
  companyName?: string
}

export const ChatBotMessageList: React.FC<ChatBotMessageListProps> = ({ messages, botIcon, loading, companyName }) => {
  const noQuestionsYet = messages.length < 2

  const messageListRef = useRef<HTMLDivElement>(null)

  //scroll to bottom of chat
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight
    }
  }, [messages])

  return (
    <Box
      ref={messageListRef}
      sx={{
        px: { xs: 1, md: 2 },
        scrollbarGutter: "stable",
        overflowY: "auto",
        flex: 1,
        minHeight: 0,
        overflowWrap: "anywhere",
        ...scrollbarMixin()
      }}
    >
      <>
        {messages.map((message, index) => {
          const msg =
            message.type === "apiMessage" ? (
              <>
                <Box sx={{ display: "flex", p: { xs: 1, md: 2 } }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <BotIcon botIcon={botIcon} size={noQuestionsYet ? "medium" : "small"} />
                  </Box>
                  <BotMessage message={message.message} makeFirstMessageBigger={false} index={index} chatbot={true} />
                </Box>

                <Box>
                  {message.provider && companyName && (
                    <ProviderCard provider={message.provider} companyName={companyName} />
                  )}
                </Box>
              </>
            ) : (
              <UserMessage message={message.message} index={index} />
            )

          return <Box key={`chatMessage-${index}`}>{msg}</Box>
        })}
        {loading && (
          <Box sx={{ display: "flex", p: { xs: 1, md: 2 } }}>
            <Box sx={{ flexShrink: 0 }}>
              <BotIcon botIcon={botIcon} size={"small"} />
            </Box>
            <Box
              sx={{
                backgroundColor: "#F9F9F9",
                borderRadius: "0px 15px 15px 15px",
                px: { xs: 2 },
                py: { xs: 2 },
                alignItems: "center",
                marginTop: "20px"
              }}
            >
              <LoadingDots />
            </Box>
          </Box>
        )}
      </>
    </Box>
  )
}

const ProviderCard = ({ provider, companyName }: { provider: Provider; companyName: string }) => {
  const { palette } = useTheme()

  const handleClick = () => {
    if (provider.url) {
      window.open(provider.url, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        border: `1px solid ${palette.primary.main}`,
        borderRadius: "15px",
        overflow: "hidden",
        maxWidth: { xs: "100%", sm: "400px", md: "450px" },
        cursor: provider.url ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": provider.url
          ? {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }
          : {},
        my: 1,
        marginLeft: { xs: 1, md: 2 }
      }}
    >
      {/* Left section - Yellow background with text */}
      <Box
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.text.primary,
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: "12px",
            textAlign: "center",
            lineHeight: "14px"
          }}
        >
          {companyName}
          <br />
          industry
          <br />
          partner:
        </Typography>
      </Box>

      {/* Right section - White background with logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          p: 1,
          px: "20px",
          "& img": {
            filter: "grayscale(100%)",
            opacity: 0.6,
            transition: "filter 0.3s, opacity 0.3s"
          },
          "&:hover img": {
            filter: "grayscale(0%)",
            opacity: 1
          }
        }}
      >
        <img
          src={provider.logo}
          alt={provider.company}
          style={{
            maxWidth: "100%",
            maxHeight: "50px",
            height: "auto",
            objectFit: "contain"
          }}
          onError={(e) => {
            // Fallback if image fails to load
            e.currentTarget.style.display = "none"
            const parent = e.currentTarget.parentElement
            if (parent) {
              const textNode = document.createElement("span")
              textNode.textContent = provider.company
              textNode.style.fontWeight = "600"
              textNode.style.fontSize = "16px"
              parent.appendChild(textNode)
            }
          }}
        />
      </Box>
    </Box>
  )
}
